import React from 'react';
import request from 'superagent';
import './../ff001-starstruck-lines/ff001-starstruck.css';
import { wrap } from '../../helpers';
import { Reel } from '../reel/reel';
import {
  addCoinSpin,
  cellKey,
  clone,
  createBlankEntry,
  createBlankState,
  defaultPlacement,
  deserializeScenarioQueue,
  findReelIndexesForCoinCount,
  getEligibleCoinDefinitions,
  getScenarioLabel,
  getVisibleCoinCells,
  getVisibleWindow,
  isNaturalTrigger,
  placementForDefinition,
  projectResponsePlans,
  reconcileEntry,
  reelStripsForMode,
  scriptEntry,
  serializeGameInput,
  serializeScenarioQueue,
  sortPlacements,
  TFf037Cell,
  TFf037CoinDefinition,
  TFf037Config,
  TFf037EditableEntry,
  TFf037EditorState,
  TFf037Modifier,
  TFf037Placement,
  TFf037ResponsePlan,
  TFf037Scenario,
} from './helpers';
import './style.css';
import { findFf037BigWin } from './big-win';

type TPreset = {
  _id: string;
  scenarios: TFf037Scenario[];
  gameInput?: Record<string, unknown>;
};

export const responseErrorMessage = (body: any, fallback: string) =>
  body?.error?.message || body?.message || (typeof body?.error === 'string' ? body.error : '') || fallback;

const requestErrorMessage = (error: any, fallback: string) =>
  responseErrorMessage(error?.response?.body, error?.message || fallback);

const definitionLabel = (definition: TFf037CoinDefinition) => {
  if (definition.kind === 'price') return `${definition.baseValue ?? definition.id} credits`;
  if (definition.kind === 'fixed-jackpot') return `${(definition.jackpotType || definition.id).toUpperCase()} Jackpot`;
  return definition.kind[0].toUpperCase() + definition.kind.slice(1);
};

const PlacementFields = (props: {
  placement: TFf037Placement;
  trigger: boolean;
  config: TFf037Config;
  activeModifiers: TFf037Modifier[];
  onChange: (placement: TFf037Placement) => void;
}) => {
  const definitions = getEligibleCoinDefinitions(props.config, props.activeModifiers, props.trigger);
  const definition = definitions.find(candidate => candidate.id === props.placement.coinId)!;
  const modifierValues = definition?.kind === 'boost'
    ? props.config.coinFeature.boostValues
    : definition?.kind === 'multi'
      ? props.config.coinFeature.multiValues
      : [];
  return (
    <div className="ff037-placement-fields">
      <label>
        Coin type
        <select
          aria-label={`Coin type ${props.placement.reel}-${props.placement.row}`}
          value={props.placement.coinId}
          onChange={event => {
            const next = definitions.find(candidate => candidate.id === event.target.value)!;
            props.onChange(placementForDefinition(props.placement, next, props.config, props.activeModifiers, props.trigger));
          }}
        >
          {definitions.map(candidate => (
            <option value={candidate.id} key={candidate.id}>{definitionLabel(candidate)}</option>
          ))}
        </select>
      </label>
      {modifierValues.length > 0 && (
        <label>
          Modifier
          <select
            aria-label={`Modifier value ${props.placement.reel}-${props.placement.row}`}
            value={props.placement.modifierValue}
            onChange={event => props.onChange({ ...props.placement, modifierValue: Number(event.target.value) })}
          >
            {modifierValues.map(value => <option value={value} key={value}>
              {definition.kind === 'boost' ? `+${value}× bet` : `×${value}`}
            </option>)}
          </select>
        </label>
      )}
      {modifierValues.length > 0 && props.config.coinFeature.modifierConversionChance && (
        <label>
          Starting prize
          <select
            aria-label={`Starting prize ${props.placement.reel}-${props.placement.row}`}
            value={props.placement.prizeCoinId ?? ''}
            onChange={event => props.onChange({ ...props.placement, prizeCoinId: event.target.value || undefined })}
          >
            <option value="">Random starting prize</option>
            {definitions.filter(coin => coin.kind === 'price' || coin.kind === 'fixed-jackpot').map(coin => (
              <option value={coin.id} key={coin.id}>{definitionLabel(coin)}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
};

const ResponsePlanEditor = (props: {
  planIndex: number;
  plan: TFf037ResponsePlan;
  entry: TFf037EditableEntry;
  config: TFf037Config;
  onChange: (plan: TFf037ResponsePlan) => void;
}) => {
  const trigger = props.planIndex === 0;
  const projections = projectResponsePlans(props.entry, props.config);
  const occupiedBefore = new Set((props.planIndex > 0 ? projections[props.planIndex - 1]?.occupied : []).map(cellKey));
  const selected = new Map(props.plan.placements.map(placement => [cellKey(placement), placement]));
  const after = projections[props.planIndex];

  const toggle = (cell: TFf037Cell) => {
    if (trigger || occupiedBefore.has(cellKey(cell))) return;
    const exists = selected.has(cellKey(cell));
    props.onChange({
      placements: exists
        ? props.plan.placements.filter(placement => cellKey(placement) !== cellKey(cell))
        : sortPlacements([
          ...props.plan.placements,
          defaultPlacement(cell, props.config, props.entry.activeModifiers, false),
        ]),
    });
  };

  return (
    <section className="ff037-plan">
      <h4>{trigger ? 'Trigger Coin assignments' : `Coin Spin ${props.planIndex}`}</h4>
      <p className="ff037-plan-status">
        {trigger
          ? 'Entry board, including any coins added by a forced trigger.'
          : `${props.plan.placements.length} landing(s) · ${after?.respinsRemaining ?? 0} respin(s) remaining`}
      </p>
      <div className="ff037-board" aria-label={`FF037 response plan ${props.planIndex}`}>
        {Array.from({ length: props.config.reelsSetup.length }, (_unused, reel) => (
          <div className="ff037-reel" key={reel}>
            {Array.from({ length: props.config.reelsSetup[reel] }, (_unusedRow, row) => {
              const cell = { reel, row };
              const placement = selected.get(cellKey(cell));
              const prior = occupiedBefore.has(cellKey(cell));
              return (
                <div
                  className={`ff037-cell ${placement ? 'selected' : ''} ${prior ? 'occupied' : ''}`}
                  key={cellKey(cell)}
                >
                  <button
                    type="button"
                    aria-label={`Toggle Coin cell ${reel}-${row} in plan ${props.planIndex}`}
                    disabled={trigger || prior}
                    onClick={() => toggle(cell)}
                  >
                    <span>R{reel + 1} · {row + 1}</span>
                    <strong>{prior ? 'Held' : placement ? 'Coin' : 'Empty'}</strong>
                  </button>
                  {placement && (
                    <PlacementFields
                      placement={placement}
                      trigger={trigger}
                      config={props.config}
                      activeModifiers={props.entry.activeModifiers}
                      onChange={next => props.onChange({
                        placements: props.plan.placements.map(value =>
                          cellKey(value) === cellKey(next) ? next : value),
                      })}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {after?.terminalReason && (
        <div className="ff037-message success">
          {after.terminalReason === 'full-grid' ? 'Full grid · feature terminates' : 'Respins exhausted · feature terminates'}
        </div>
      )}
    </section>
  );
};

const EntryEditor = (props: {
  entry: TFf037EditableEntry;
  index: number;
  state: TFf037EditorState;
  config: TFf037Config;
  onChange: (entry: TFf037EditableEntry) => void;
}) => {
  const [coinSpinError, setCoinSpinError] = React.useState('');
  const [bigWinNotice, setBigWinNotice] = React.useState<{ text: string; selection: string }>();
  const selectionKey = (indexes: number[]) => `${props.state.mode}:${indexes.join(',')}`;
  const strips = reelStripsForMode(props.config, props.state.mode);
  const window = getVisibleWindow(props.entry.reelIndexes, props.config, props.state.mode);
  const coinCells = getVisibleCoinCells(window, props.config);
  const trigger = isNaturalTrigger(props.entry.reelIndexes, props.config, props.state.mode);
  const forcedTrigger = props.entry.forceCoinSpinsTrigger === true &&
    coinCells.length > 0 && coinCells.length < props.config.coinFeature.coinTrigger;
  const projections = projectResponsePlans(props.entry, props.config);
  const terminal = projections[projections.length - 1]?.terminalReason;

  const updatePlan = (planIndex: number, plan: TFf037ResponsePlan) => props.onChange(reconcileEntry({
    ...props.entry,
    responsePlans: props.entry.responsePlans.map((value, index) => index === planIndex ? plan : value),
  }, props.config, props.state.mode));

  return (
    <section className="spin ff037-entry">
      <h2>Queued base play {props.index + 1}</h2>
      <div className="ff037-actions">
        <button type="button" className="btn-freespins-action"
          title="Find the highest-paying full-screen symbol available on these reels"
          onClick={() => {
            const match = findFf037BigWin(props.config, props.state.mode);
            if (!match) {
              setBigWinNotice({ text: 'No full-screen result reaching the configured Big Win level was found.',
                selection: selectionKey(props.entry.reelIndexes) });
              return;
            }
            props.onChange(reconcileEntry({ ...props.entry, reelIndexes: match.reelIndexes,
              forceCoinSpinsTrigger: false, scripted: false, responsePlans: [],
            }, props.config, props.state.mode));
            setCoinSpinError('');
            setBigWinNotice({ text: `Big Win: ${match.multiplier.toLocaleString('en-US', { maximumFractionDigits: 2 })}× base bet (${match.winLevel}).`,
              selection: selectionKey(match.reelIndexes) });
          }}>Big Win</button>
        <button type="button" className="btn-freespins-action" onClick={() => {
          const stops = findReelIndexesForCoinCount(props.config, props.state.mode, 1,
            props.config.coinFeature.coinTrigger - 1);
          if (stops) props.onChange(reconcileEntry({ ...props.entry, reelIndexes: stops }, props.config, props.state.mode));
        }}>Find 1–5 coins</button>
        <button type="button" className="btn-freespins-action" onClick={() => {
          const stops = findReelIndexesForCoinCount(
            props.config,
            props.state.mode,
            props.config.coinFeature.coinTrigger,
          );
          if (stops) props.onChange(reconcileEntry({ ...props.entry, reelIndexes: stops }, props.config, props.state.mode));
        }}>Find 6+ Coin trigger</button>
        <button type="button" className="btn-freespins-action" onClick={() => {
          const stops = findReelIndexesForCoinCount(props.config, props.state.mode, 0);
          if (stops) props.onChange(reconcileEntry({ ...props.entry, reelIndexes: stops }, props.config, props.state.mode));
        }}>Reset stops</button>
      </div>
      {bigWinNotice?.selection === selectionKey(props.entry.reelIndexes) &&
        <div className="ff037-message" role="status">{bigWinNotice.text}</div>}
      <div className="reelContainer">
        {props.entry.reelIndexes.map((reelIndex, index) => (
          <Reel
            key={index}
            index={index}
            rows={props.config.reelsSetup[index]}
            reelIndex={reelIndex}
            reelStrips={strips[index]}
            setReelIndex={({ reelIndex: reel, dir, forcedIndex }) => {
              const requested = typeof forcedIndex === 'number'
                ? forcedIndex
                : props.entry.reelIndexes[reel] + (dir || 1);
              const wrapped = wrap(requested, strips[reel]);
              const normalized = wrapped < 0 ? strips[reel].length + wrapped : wrapped;
              props.onChange(reconcileEntry({
                ...props.entry,
                reelIndexes: props.entry.reelIndexes.map((old, current) => current === reel ? normalized : old),
              }, props.config, props.state.mode));
            }}
          />
        ))}
      </div>
      <div className="ff037-window" aria-label={`FF037 base window ${props.index}`}>
        {window.map((reel, reelIndex) => (
          <div className="ff037-reel" key={reelIndex}>
            {reel.map((symbol, row) => (
              <div className={`ff037-symbol ${symbol === props.config.coinFeature.coin ? 'coin' : ''}`} key={`${reelIndex}:${row}`}>
                <span>R{reelIndex + 1} · {row + 1}</span><strong>{symbol}</strong>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className={`ff037-message ${trigger || forcedTrigger ? 'success' : ''}`}>
        {coinCells.length} Coin(s) · {trigger ? 'natural Hold & Win trigger'
          : forcedTrigger ? 'forced Hold & Win trigger' : 'no deterministic natural trigger'}
      </div>
      <fieldset className="ff037-controls">
        <legend>Hold & Win behavior</legend>
        <label>
          <input
            type="checkbox"
            aria-label={`Force Coin Spins trigger (1–5 coins) ${props.index}`}
            checked={props.entry.forceCoinSpinsTrigger === true}
            onChange={event => props.onChange(reconcileEntry({
              ...props.entry,
              forceCoinSpinsTrigger: event.target.checked,
            }, props.config, props.state.mode))}
          />
          Force Coin Spins trigger (1–5 coins)
        </label>
        {props.entry.forceCoinSpinsTrigger && <p>
          With 1–5 landed coins, force Hold & Win and use the normal fill-to-six and modifier selection.
          Enable scripted Coin responses below to choose the six-Coin entry board and feature spins.
        </p>}
        <label>
          <input
            type="checkbox"
            aria-label={`Script Hold and Win ${props.index}`}
            disabled={!trigger && !forcedTrigger}
            checked={props.entry.scripted}
            onChange={event => props.onChange(event.target.checked
              ? scriptEntry(props.entry, props.config, props.state.mode)
              : { ...props.entry, scripted: false, responsePlans: [] })}
          />
          Script deterministic Coin responses
        </label>
        {forcedTrigger && props.entry.scripted && <p>The trigger plan includes the fill to six and 1–3 distinct modifier Coins. One Collect is kept by default.</p>}
      </fieldset>
      <h3>Coin Spins</h3>
      {!props.entry.scripted && <p>
        Add Coin Spins to set up the feature and edit its spins. A 1–5 Coin window will be forced to trigger;
        a window without Coins will be replaced with a trigger window.
      </p>}
      <div className="ff037-actions">
        <button
          type="button"
          className="btn-freespins-action"
          disabled={!!terminal || props.entry.responsePlans.length >= 64}
          onClick={() => {
            const next = addCoinSpin(props.entry, props.config, props.state.mode);
            setCoinSpinError(next ? '' : 'No Coin trigger window is available in this mode.');
            if (next) props.onChange(next);
          }}
        >{props.entry.scripted ? 'Add Coin Spin' : 'Add Coin Spins'}</button>
        {props.entry.scripted && <button
          type="button"
          className="btn-freespins-action"
          disabled={props.entry.responsePlans.length <= 1}
          onClick={() => props.onChange({
            ...props.entry,
            responsePlans: props.entry.responsePlans.slice(0, -1),
          })}
        >Remove Coin Spin</button>}
      </div>
      {coinSpinError && <div className="ff037-message error" role="alert">{coinSpinError}</div>}
      {props.entry.scripted && (
        <>
          <p>Click an empty cell to add a Coin, then choose its type and value. Held cells carry over from earlier spins.
            An empty spin uses one respin; landing a Coin resets the counter.</p>
          <p>Collect, Multi, and Boost are always available during Hold & Win. Mystery is a reveal animation.</p>
          {props.config.coinFeature.modifierConversionChance && <p>
            BOOST and MULTI keep a price or jackpot starting prize. Choose the modifier and its final reveal value;
            the client keeps the prize blank until bonus activation, shuffles the modifier for 4 seconds, then
            reveals its own modified prize before applying the effect to other eligible coins. Later modifiers can affect these prizes.
            {props.config.coinFeature.modifiersAffectHeldCollects
              ? ' BOOST and MULTI also affect held COLLECTs; new COLLECTs activate last and become eligible next spin.'
              : ' COLLECT remains unaffected by BOOST and MULTI.'}
            Scripted choices override the random modifier roll.
          </p>}
          {props.entry.responsePlans.map((plan, planIndex) => (
            <ResponsePlanEditor
              key={planIndex}
              planIndex={planIndex}
              plan={plan}
              entry={props.entry}
              config={props.config}
              onChange={next => updatePlan(planIndex, next)}
            />
          ))}
        </>
      )}
    </section>
  );
};

export const Model = ({ modelId }: { modelId: string }) => {
  const [config, setConfig] = React.useState<TFf037Config>();
  const [state, setState] = React.useState<TFf037EditorState>();
  const [presets, setPresets] = React.useState<TPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = React.useState('');
  const [userId, setUserId] = React.useState(localStorage.getItem('userid') || '');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get-config`)
        .withCredentials().send({ modelId }),
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get`)
        .withCredentials().send({ modelId }),
    ]).then(([configRes, scenariosRes]) => {
      if (cancelled) return;
      if (!configRes.body?.success || !configRes.body?.config) {
        throw new Error(responseErrorMessage(configRes.body, 'Error loading FF037 config'));
      }
      if (!scenariosRes.body?.success || !Array.isArray(scenariosRes.body?.scenarios)) {
        throw new Error(responseErrorMessage(scenariosRes.body, 'Error loading FF037 scenarios'));
      }
      const loadedConfig = configRes.body.config as TFf037Config;
      setConfig(loadedConfig);
      setPresets(scenariosRes.body.scenarios || []);
      setState(createBlankState(loadedConfig));
    }).catch(caught => { if (!cancelled) setError(requestErrorMessage(caught, 'Error loading FF037 editor')); });
    return () => { cancelled = true; };
  }, [modelId]);

  if (!config || !state) return <>{error ? <div role="alert">{error}</div> : 'Loading'}</>;
  const queue = serializeScenarioQueue(state);
  const gameInput = serializeGameInput(state);
  const unsupported = !!state.unsupportedQueue;
  const payload = () => ({ modelId, scenarios: queue, ...(gameInput ? { gameInput } : {}) });
  const showError = (message: string) => { setNotice(''); setError(message); };
  const showNotice = (message: string) => { setError(''); setNotice(message); };

  const save = async () => {
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/upsert`)
        .withCredentials().send({ ...payload(), _id: selectedPreset || undefined })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error saving scenario'));
      const id = body._id || selectedPreset || modelId;
      setSelectedPreset(id);
      setPresets(current => [
        ...current.filter(preset => preset._id !== id),
        { _id: id, scenarios: clone(queue), ...(gameInput ? { gameInput: clone(gameInput) } : {}) },
      ]);
      showNotice('Scenario saved');
    } catch (caught) { showError(requestErrorMessage(caught, 'Error saving scenario')); }
  };

  const del = async () => {
    if (!selectedPreset || !window.confirm('Confirm deletion')) return;
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/delete`)
        .withCredentials().send({ modelId, _id: selectedPreset })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error deleting scenario'));
      setPresets(current => current.filter(preset => preset._id !== selectedPreset));
      setSelectedPreset('');
      setState(createBlankState(config));
      showNotice('Scenario deleted');
    } catch (caught) { showError(requestErrorMessage(caught, 'Error deleting scenario')); }
  };

  const play = async () => {
    if (!userId.trim()) return showError('Please enter a user id');
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/play-user`)
        .withCredentials().send({ ...payload(), gameId: modelId, userPublicKey: userId })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error queueing scenario'));
      showNotice(`Scenario queued for play (${body.queueLength ?? queue.length} entry/entries)`);
    } catch (caught) { showError(requestErrorMessage(caught, 'Error queueing scenario')); }
  };

  const selectPreset = (id: string) => {
    setSelectedPreset(id);
    setError('');
    setNotice('');
    const preset = presets.find(item => item._id === id);
    setState(preset
      ? deserializeScenarioQueue(clone(preset.scenarios), preset.gameInput ? clone(preset.gameInput) : undefined, config)
      : createBlankState(config));
  };

  return (
    <main className="ff001-container ff037-container">
      <h1>{modelId}</h1>
      <p className="ff037-note">Development scenarios are process-local, expire automatically, and are unavailable in production.</p>
      <div className="scenario-presets">
        <h3>Scenario presets</h3>
        <select aria-label="FF037 scenario preset" value={selectedPreset} onChange={event => selectPreset(event.target.value)}>
          <option value="" />
          {presets.map(preset => {
            const presetState = deserializeScenarioQueue(preset.scenarios, preset.gameInput, config);
            return <option value={preset._id} key={preset._id}>{preset._id} · {getScenarioLabel(presetState, config)}</option>;
          })}
        </select>
      </div>
      {unsupported ? (
        <div className="ff037-message error" role="alert">
          {state.unsupportedReason} The native payload is preserved exactly and is read-only.
        </div>
      ) : (
        <>
          <fieldset className="ff037-controls">
            <legend>Paid spin mode</legend>
            <label>
              Mode
              <select
                aria-label="FF037 paid spin mode"
                value={state.mode}
                onChange={event => setState({
                  mode: event.target.value as 'standard' | 'boosted',
                  entries: state.entries.map(() => createBlankEntry(config)),
                })}
              >
                <option value="standard">Standard (x{config.betMultiplier})</option>
                <option value="boosted">Boosted Spins (x{config.betMultiplierBoosted})</option>
              </select>
            </label>
          </fieldset>
          {state.entries.map((entry, index) => (
            <EntryEditor
              key={index}
              entry={entry}
              index={index}
              state={state}
              config={config}
              onChange={next => setState(current => current && ({
                ...current,
                entries: current.entries.map((value, entryIndex) => entryIndex === index ? next : value),
              }))}
            />
          ))}
          <div className="ff037-actions">
            <button type="button" className="btn-freespins-action" onClick={() => setState(current => current && ({
              ...current,
              entries: [...current.entries, createBlankEntry(config)],
            }))}>Add queued play</button>
            <button
              type="button"
              className="btn-freespins-action"
              disabled={state.entries.length <= 1}
              onClick={() => setState(current => current && ({ ...current, entries: current.entries.slice(0, -1) }))}
            >Remove queued play</button>
          </div>
        </>
      )}
      <details className="ff037-json-preview">
        <summary>Read-only native JSON preview</summary>
        <textarea
          aria-label="Native FF037 scenario JSON"
          readOnly
          value={JSON.stringify({ scenarios: queue, ...(gameInput ? { gameInput } : {}) }, null, 2)}
        />
      </details>
      <div className="ff001-userid">
        <h3>User Public Key</h3>
        <input aria-label="User Public Key" value={userId} onChange={event => {
          setUserId(event.target.value);
          localStorage.setItem('userid', event.target.value);
        }} />
      </div>
      {error && <div className="ff037-message error" role="alert">{error}</div>}
      {notice && <div className="ff037-message success" role="status">{notice}</div>}
      <div className="model-buttons">
        <button className="btn-save" type="button" onClick={() => {
          setSelectedPreset('');
          setState(createBlankState(config));
          setError('');
          setNotice('');
        }}>New</button>
        <button className="btn-save" type="button" disabled={unsupported} onClick={save}>Save</button>
        {selectedPreset && <button className="btn-save" type="button" onClick={del}>Delete</button>}
        <button className="btn-save btn-play" type="button" disabled={unsupported} onClick={play}>Play</button>
      </div>
    </main>
  );
};

export default Model;
